import classnames from "classnames";
import { CSSProperties, ChangeEvent, useEffect, useRef, useState } from "react";

import { CustomSelectProps, CustomSelectType, OptionType } from "./types";
import MySelectDropdown from "./drop-down";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { Popover, Skeleton, Tag, message, prefixCls } from "@payping/web-components";
import { getOptionLabel, handleDeselect, handleSelect } from "./utils";

const SelectCreate: CustomSelectType = (props: CustomSelectProps) => {
  const {
    options,
    onSelect,
    onChange,
    onDeselect,
    value,
    create,
    category,
    placeholder,
    onSearch,
    filterOption,
    dropdownProps,
    loading,
    inlineCreate,
    skeleton,
    size,
    style,
    className,
    ...otherProps
  } = props;
  const [openDropdown, setOpenDropdown] = useState<boolean>();
  const [allSelected, setAllSelected] = useState<OptionType[]>();
  const [activeOption, setActiveOption] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>("");
  const selectRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTextRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const { t } = useAppTranslation();

  function defaultFilterOption(searchText: string, option: OptionType) {
    return option.title ? option.title.indexOf(searchText) >= 0 : false;
  }

  const filteredCategoryOptions =
    searchValue && category?.filterOption
      ? category.options?.filter((item) =>
          category.filterOption === true
            ? defaultFilterOption(searchValue, item)
            : category.filterOption?.(searchValue, item),
        )
      : category?.options;
  const filteredOptions =
    searchValue && filterOption
      ? options?.filter((item) =>
          filterOption === true ? defaultFilterOption(searchValue, item) : filterOption(searchValue, item),
        )
      : options;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAllSelected(value);
  }, [value]);

  useEffect(() => {
    onSearch?.(searchValue);
    // This function should not contain onSearch in its dependency array because of infinite rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  return skeleton ? (
    <Skeleton.Input size={size === "medium" ? "default" : size} className={className} style={style as CSSProperties} />
  ) : (
    <div style={{ width: "100%" }}>
      <div
        onClick={() => setOpenDropdown(false)}
        className={`${prefixCls}-custom-select-overlay`}
        style={{ display: openDropdown ? "block" : "none" }}
      />
      <Popover
        open={openDropdown}
        classNames={{ root: `${prefixCls}-custom-select-dropdown` }}
        // @ts-ignore: missing
        // eslint-disable-next-line react-hooks/refs
        styles={{ root: { width: selectRef.current?.offsetWidth } }}
        content={
          <MySelectDropdown
            create={create}
            category={category}
            allSelected={allSelected}
            setAllSelected={setAllSelected}
            setVisibleDropdown={setOpenDropdown}
            setActiveOption={setActiveOption}
            setSearchValue={setSearchValue}
            activeOption={activeOption}
            filteredCategoryOptions={filteredCategoryOptions}
            filteredOptions={filteredOptions}
            loading={loading}
            overlayRef={overlayRef}
            inputRef={inputRef}
            onSelect={onSelect}
            onChange={onChange}
            onDeselect={onDeselect}
            {...dropdownProps}
          />
        }
        placement={"bottom"}
      >
        <div
          ref={selectRef}
          className={classnames(
            `${prefixCls}-custom-select`,
            { [`${prefixCls}-custom-select-lg`]: size === "large" },
            // eslint-disable-next-line react-hooks/refs
            inputRef.current === document.activeElement && `${prefixCls}-custom-select-focused`,
            className,
          )}
          // onFocus={(event: FocusEvent<HTMLDivElement>) => {
          //   setVisibleDropdown(true);
          // }}
          onClick={() => {
            setOpenDropdown((prev) => !prev);
            if (document.activeElement !== inputRef.current) inputRef.current?.focus();
          }}
          style={style}
          {...otherProps}
        >
          <div className={`${prefixCls}-custom-select-selected-options`}>
            {allSelected?.map((item) => (
              <Tag
                key={String(item.value)}
                closable
                onClose={() =>
                  setAllSelected(
                    handleDeselect({
                      allSelected,
                      isCategory: item.type === "category",
                      onChange,
                      onDeselect: item.type === "category" ? category?.onDeselect : onDeselect,
                      option: item,
                    }),
                  )
                }
                color={item.type === "category" ? "processing" : undefined}
              >
                {getOptionLabel(item)}
              </Tag>
            ))}
            <div ref={searchTextRef} className={`${prefixCls}-custom-select-hidden-input`}>
              {searchValue}
            </div>
            <div
              className={`${prefixCls}-custom-select-input-wrapper`}
              // eslint-disable-next-line react-hooks/refs
              style={{ width: searchTextRef.current?.offsetWidth ? searchTextRef.current.offsetWidth + 2 : 5 }}
            >
              <input
                ref={inputRef}
                className={`${prefixCls}-custom-select-input`}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setOpenDropdown(true);
                  setSearchValue(event.currentTarget.value);
                }}
                onKeyDown={async (event) => {
                  if (event.keyCode === 32 && !event.currentTarget.value) {
                    event.preventDefault();
                  }
                  // Prevent calling form.submit when Enter is pressed
                  if (event.keyCode === 13) {
                    event.preventDefault();
                  }
                  // if ([13, 32].includes(event.keyCode)) {
                  //   // event.preventDefault();
                  //   if (loading) {
                  //     Message.warning({ content: t("components.select.error.waitForLoading") });
                  //   } else {
                  //     inlineCreate?.(event.currentTarget.value);
                  //   }
                  // }
                }}
                onKeyUp={async (event) => {
                  // event.preventDefault();
                  // 13: Enter, 32: Space
                  if ([13, 32].includes(event.keyCode)) {
                    if (loading) {
                      message.warning({ content: t("components.select.error.waitForLoading") });
                    } else {
                      const isCategory = filteredOptions?.length ? activeOption >= filteredOptions.length : false;
                      const exists = allSelected?.find((item) =>
                        isCategory
                          ? item.value ===
                            filteredCategoryOptions?.[activeOption - (filteredOptions?.length || 0)]?.value
                          : item.value === filteredOptions?.[activeOption]?.value,
                      );
                      let option;
                      if (isCategory) option = filteredCategoryOptions?.[activeOption - (filteredOptions?.length || 0)];
                      else if (filteredOptions?.[activeOption]) option = filteredOptions?.[activeOption];
                      if (exists && option) {
                        handleDeselect({
                          allSelected,
                          isCategory,
                          onChange,
                          onDeselect: isCategory ? category?.onDeselect : onDeselect,
                          option,
                        });
                      } else if (option) {
                        handleSelect({
                          allSelected,
                          onChange,
                          onSelect: isCategory ? category?.onSelect : onSelect,
                          option,
                        });
                        if (inputRef.current) inputRef.current.value = "";
                        setSearchValue("");
                      } else if (searchValue) {
                        inlineCreate?.(searchValue);
                        if (inputRef.current) inputRef.current.value = "";
                        setSearchValue("");
                      }
                    }
                  }
                  // Disable removing selected options by backspace for now
                  // 8: Backspace key
                  // if (event.keyCode === 8 && !searchValue) {
                  //   if (allSelected?.length)
                  //     handleDeselect({
                  //       allSelected,
                  //       onChange,
                  //       onDeselect:
                  //         allSelected[allSelected.length - 1].type === "category" ? category?.onDeselect : onDeselect,
                  //       option: allSelected[allSelected.length - 1],
                  //     });
                  // }
                  // 27: Escape key
                  if (event.keyCode === 27) {
                    setOpenDropdown(false);
                  }
                  // 38: Up arrow, 40: Down arrow
                  if ([38, 40].includes(event.keyCode)) {
                    setOpenDropdown(true);
                  }
                  const len = (filteredOptions?.length || 0) + (filteredCategoryOptions?.length || 0);
                  // 40: Down arrow
                  if (event.keyCode === 40) {
                    setActiveOption((prev) => (prev + 1) % len);
                  }
                  // 38: Up arrow
                  if (event.keyCode === 38) {
                    setActiveOption((prev) => (prev - 1 + len) % len);
                  }
                }}
              />
            </div>
          </div>
          {!searchValue && !(allSelected && allSelected.length !== 0) && (
            <span className={`${prefixCls}-custom-select-placeholder`}>{placeholder}</span>
          )}
        </div>
      </Popover>
    </div>
  );
};

export { SelectCreate };
